/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_PERSIST_CACHE } from '@airasia-rn/ui';

type RecommendationBookingStateStore = {
  hasUpcomingBooking?: boolean;
};

const defaultStore: RecommendationBookingStateStore = {};

export const recommendationBookingStateKeys = {
  all: [
    QUERY_PERSIST_CACHE,
    'home',
    'recommendation',
    'booking_state',
  ] as const,
};

export const useRecommendationBookingState = ({
  hasUpcomingBooking,
  shouldPersist,
}: {
  hasUpcomingBooking?: boolean;
  shouldPersist: boolean;
}) => {
  const queryClient = useQueryClient();

  const { data: persistedBookingState = defaultStore } = useQuery({
    queryKey: recommendationBookingStateKeys.all,
    queryFn: () => Promise.resolve(defaultStore),
    initialData: defaultStore,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  useEffect(() => {
    if (!shouldPersist || typeof hasUpcomingBooking !== 'boolean') {
      return;
    }

    queryClient.setQueryData<RecommendationBookingStateStore>(
      recommendationBookingStateKeys.all,
      (previousStore) => {
        const currentStore = previousStore ?? defaultStore;

        if (currentStore.hasUpcomingBooking === hasUpcomingBooking) {
          return currentStore;
        }

        return { hasUpcomingBooking };
      },
    );
  }, [hasUpcomingBooking, queryClient, shouldPersist]);

  return {
    persistedHasUpcomingBooking: persistedBookingState.hasUpcomingBooking,
  };
};
